import { useState, useCallback, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft,
  Camera,
  FileText,
  Download,
  HelpCircle,
  Loader2,
  AlertCircle,
  RefreshCw,
  Send,
  Play,
  Pause,
  SkipBack,
  SkipForward,
  ZoomIn,
  ZoomOut,
  Plus,
  Wand2,
  CheckCircle,
  Mic,
  AlignLeft,
  Tag,
} from 'lucide-react';

import { useCurrentUser } from '../../context/UserContext';
import { AnnotationProvider, useAnnotation } from '../../context/AnnotationContext';
import { useAnnotationTask, useSubmitTask, useReviewTask, useFinalReviewTask, useReopenTask, useClaimTask, useUnclaimTask } from '../../hooks/useAnnotationTask';
import { useAutoSave } from '../../hooks/useAutoSave';
import { useVideoPlayer } from '../../hooks/useVideoPlayer';
import { useKeyboardShortcuts } from '../../hooks/useKeyboardShortcuts';
import * as annotationApi from '../../services/annotationApi';

import AutoSaveIndicator from '../../components/annotation/shared/AutoSaveIndicator';
import TimestampDisplay from '../../components/annotation/shared/TimestampDisplay';
import KeyboardShortcutsOverlay from '../../components/annotation/shared/KeyboardShortcutsOverlay';
import VideoPlayerPanel from '../../components/annotation/workspace/VideoPlayerPanel';
import TranscriptPanel from '../../components/annotation/workspace/TranscriptPanel';
import AnnotationPanel from '../../components/annotation/workspace/AnnotationPanel';
import PhotoAnnotationPanel from '../../components/annotation/workspace/PhotoAnnotationPanel';
import ReviewPanel from '../../components/annotation/review/ReviewPanel';
import FinalReviewPanel from '../../components/annotation/review/FinalReviewPanel';
import AdminRescuePanel from '../../components/annotation/review/AdminRescuePanel';
import AddSegmentModal from '../../components/annotation/utterance/AddSegmentModal';

import {
  TaskStatus,
  TASK_STATUS_LABELS,
  TASK_STATUS_COLORS,
  Utterance,
  EntityType,
  EntitySentiment,
  PhotoType,
  TranscriptionJob,
} from '../../types/annotation';

// ─── Player constants ──────────────────────────────────────────────────────────

const PLAYBACK_RATES = [
  { value: 0.5, label: '0.5×' },
  { value: 1, label: '1×' },
  { value: 1.25, label: '1.25×' },
  { value: 1.5, label: '1.5×' },
];
// 0 = WaveSurfer auto-fit. Subsequent levels grow progressively so early
// steps feel subtle and later steps provide deep zoom.
const ZOOM_LEVELS = [0, 2, 5, 15, 40, 100, 200, 400] as const;

// ─── Status Badge ──────────────────────────────────────────────────────────────

function StatusBadge({ status }: { status: TaskStatus }) {
  return (
    <span
      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${TASK_STATUS_COLORS[status]}`}
    >
      {TASK_STATUS_LABELS[status]}
    </span>
  );
}

// ─── Confirm Dialog ────────────────────────────────────────────────────────────

interface ConfirmDialogProps {
  open: boolean;
  title: string;
  message: string;
  confirmLabel: string;
  confirmClass?: string;
  onConfirm: () => void;
  onCancel: () => void;
}

function ConfirmDialog({
  open,
  title,
  message,
  confirmLabel,
  confirmClass = 'bg-violet-600 hover:bg-violet-700 text-white',
  onConfirm,
  onCancel,
}: ConfirmDialogProps) {
  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm"
      onClick={(e) => { if (e.target === e.currentTarget) onCancel(); }}
      role="dialog"
      aria-modal="true"
    >
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm mx-4 p-6">
        <h2 className="text-base font-semibold text-gray-900 mb-2">{title}</h2>
        <p className="text-sm text-gray-600 mb-5">{message}</p>
        <div className="flex gap-2">
          <button
            onClick={onCancel}
            className="flex-1 px-3 py-2 text-sm rounded-lg font-medium bg-white border border-gray-200 hover:bg-gray-50 text-gray-700 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            className={`flex-1 px-3 py-2 text-sm rounded-lg font-medium transition-colors ${confirmClass}`}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Inline Toast ──────────────────────────────────────────────────────────────

interface ToastMessage {
  id: number;
  type: 'success' | 'error';
  message: string;
}

// ─── Workspace Inner (consumes AnnotationContext) ──────────────────────────────

function WorkspaceInner({ taskId }: { taskId: string }) {
  const { role, userId: meId } = useCurrentUser();
  const qc = useQueryClient();

  const {
    state,
    setTask,
    selectUtterance,
    updateUtteranceLocal,
    addUtteranceLocal,
    removeUtteranceLocal,
    addEntityLocal,
    removeEntityLocal,
    updatePhotoLabelsLocal,
    updatePhotoTypeLocal,
    setAutoSaveStatus,
    selectedUtterance,
  } = useAnnotation();

  const { data: task, isLoading, isError, refetch } = useAnnotationTask(taskId);

  // Load task into context on first fetch
  const taskLoadedRef = useRef(false);
  useEffect(() => {
    if (task && !taskLoadedRef.current) {
      setTask(task);
      taskLoadedRef.current = true;
    } else if (task) {
      // Refresh when task changes externally (e.g. after submit)
      setTask(task);
    }
  }, [task, setTask]);

  const submitMutation = useSubmitTask();
  const reviewMutation = useReviewTask();
  const finalReviewMutation = useFinalReviewTask();
  const reopenMutation = useReopenTask();
  const claimMutation = useClaimTask();
  const unclaimMutation = useUnclaimTask();

  // Player refs — video element stays in the left panel; waveform stretches full-width in the top bar
  const videoRef = useRef<HTMLVideoElement>(null);
  const waveformBarRef = useRef<HTMLDivElement>(null);

  const [addSegmentOpen, setAddSegmentOpen] = useState(false);
  const [addSegmentStart, setAddSegmentStart] = useState(0);
  const [addSegmentEnd, setAddSegmentEnd] = useState(5);
  const [shortcutsOpen, setShortcutsOpen] = useState(false);
  const [showPhotos, setShowPhotos] = useState(false);
  const [showTranscripts, setShowTranscripts] = useState(false);
  const [submitConfirmOpen, setSubmitConfirmOpen] = useState(false);
  const [toasts, setToasts] = useState<ToastMessage[]>([]);
  const [mobilePanel, setMobilePanel] = useState<'transcript' | 'annotation'>('transcript');
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 640);
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 639px)');
    const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);
  const toastCounterRef = useRef(0);

  // ── Auto-transcription state ─────────────────────────────────────────────
  const [transcriptionJob, setTranscriptionJob] = useState<TranscriptionJob | null>(null);
  const [triggerPending, setTriggerPending] = useState(false);
  const [transcribeDialogOpen, setTranscribeDialogOpen] = useState(false);
  const [transcribeLang, setTranscribeLang] = useState('hi');     // default Hindi; override per re-transcribe
  const [transcribeMaxSpk, setTranscribeMaxSpk] = useState('');   // '' → auto speaker count
  const transcriptionStatus = transcriptionJob?.status ?? state.task?.transcriptionStatus ?? 'none';
  const isTranscribing = transcriptionStatus === 'queued' || transcriptionStatus === 'running';

  // Seed initial job status from the task once loaded
  useEffect(() => {
    if (state.task && transcriptionJob === null && state.task.transcriptionStatus !== 'none') {
      annotationApi.getTranscriptionJob(taskId).then(setTranscriptionJob).catch(() => null);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.task?.taskId]);

  // Poll every 10 s while a job is running/queued
  useEffect(() => {
    if (!isTranscribing) return;
    const interval = setInterval(async () => {
      try {
        const job = await annotationApi.getTranscriptionJob(taskId);
        setTranscriptionJob(job);
        if (job && (job.status === 'completed' || job.status === 'failed')) {
          // Refresh the task to pull in the new utterances
          refetch();
        }
      } catch {
        // Ignore poll errors
      }
    }, 10_000);
    return () => clearInterval(interval);
  }, [isTranscribing, taskId, refetch]);


  const addToast = useCallback((type: 'success' | 'error', message: string) => {
    const id = ++toastCounterRef.current;
    setToasts((prev) => [...prev, { id, type, message }]);
    setTimeout(() => setToasts((prev) => prev.filter((t) => t.id !== id)), 3500);
  }, []);

  const handleTriggerTranscription = useCallback(async (opts?: { language?: string; maxSpeakers?: number }) => {
    setTriggerPending(true);
    try {
      const result = await annotationApi.triggerTranscription(taskId, opts ?? {});
      setTranscriptionJob((prev) => prev
        ? { ...prev, status: 'queued' as const }
        : {
            jobId: result.jobId, taskId, status: 'queued' as const,
            modelName: 'faster-whisper-large-v3', language: opts?.language ?? 'auto',
            maxSpeakers: opts?.maxSpeakers ?? 5,
            numSpeakersDetected: null, s3OutputKey: null, triggeredBy: '',
            triggeredAt: new Date().toISOString(), startedAt: null, completedAt: null,
            audioDurationS: null, processingTimeS: null, errorMessage: null,
            createdAt: new Date().toISOString(),
          });
      addToast('success', 'Auto-transcription started. Segments will appear once complete.');
    } catch (err) {
      addToast('error', err instanceof Error ? err.message : 'Failed to start transcription.');
    } finally {
      setTriggerPending(false);
    }
  }, [taskId, addToast]);

  // Serialize text/label saves so rapid blur events cannot overwrite newer
  // corrections, and final review waits for every outstanding correction.
  const pendingEdits = useRef<Promise<void>>(Promise.resolve());
  const failedEdits = useRef(new Map<string, Partial<Utterance>>());
  const [finalizingReview, setFinalizingReview] = useState(false);

  // Role-based flags (declared before hooks that depend on them)
  // Transcribers get the same self-service capabilities as enumerators
  // (can edit, can submit). They don't get the supervisor's review actions
  // (Approve / Reject / Needs Revision) — that's gated separately on
  // isSupervisorOrAdmin below.
  const isEnumerator = role === 'enumerator' || role === 'transcriber';
  const isSupervisorOrAdmin = role === 'supervisor' || role === 'admin';
  const isSubAdminOrAdmin = role === 'sub-admin' || role === 'admin';

  const taskStatus = state.task?.status;
  // Claim lock — mirrors checkTaskEditable() on the backend. If a task is
  // claimed by someone else AND the viewer is a transcriber/enumerator, they
  // can't edit. Supervisors and admins can still edit through a claim (they
  // can also Unclaim from the banner above).
  const claimedBy = (state.task as { claimedBy?: string | null } | null)?.claimedBy ?? null;
  const claimedByOther = !!claimedBy && claimedBy !== meId;
  const claimLockedOut = claimedByOther && !isSupervisorOrAdmin && !isSubAdminOrAdmin;

  // Supervisors and admins can additionally edit utterances on 'submitted'
  // tasks so they can fix small issues before approving instead of bouncing
  // back to the transcriber. Mirrors the widened checkTaskEditable() in the
  // backend middleware (it scopes to the supervisor's subtree there).
  const canEdit =
    !!taskStatus &&
    !finalizingReview &&
    !claimLockedOut &&
    (role === 'admin' ||
      (role === 'sub-admin' && taskStatus === 'approved') ||
      (role !== 'sub-admin' && ['pending', 'in_progress', 'revision_needed'].includes(taskStatus)) ||
      (isSupervisorOrAdmin && taskStatus === 'submitted'));

  // The workspace runs in read-only "review" mode whenever the task is in
  // a state the current user can't actually persist edits to. The backend
  // (checkTaskEditable) is the source of truth and will 403 the same set
  // of cases; mirroring it here keeps the UI honest:
  //   - approved: sub-admins can correct scoped tasks during final review.
  //   - rejected / final_accepted / final_rejected: admins only.
  //   - submitted: locked for non-supervisor callers; supervisors/admins
  //     can edit. Enumerators / transcribers must wait for the supervisor
  //     to send the task back as 'revision_needed' before they can edit.
  //   - claimed by another user + viewer is transcriber/enumerator: locked.
  const isReviewMode = !canEdit;

  // Auto save
  const { saveNow } = useAutoSave({
    taskId,
    getState: () => ({
      utterances: state.task?.utterances ?? [],
    }),
    onStatusChange: (status, savedAt) => setAutoSaveStatus(status, savedAt),
    enabled:
      !!state.task &&
      ['pending', 'in_progress', 'revision_needed'].includes(state.task.status) &&
      (isEnumerator || isSupervisorOrAdmin),
  });

  // Current time and waveform player
  const [currentTime, setCurrentTime] = useState(0);

  const {
    state: playerState,
    playPause,
    skip,
    setPlaybackRate,
    zoom,
    highlightRegion,
    clearDragRegion,
    toggleSegment,
  } = useVideoPlayer({
    videoRef,
    waveformContainerRef: waveformBarRef,
    videoUrl: state.task?.videoUrl ?? undefined,
    audioUrl: state.task?.audioUrl ?? undefined,
    peaksUrl: state.task?.peaksUrl ?? undefined,
    onTimeUpdate: setCurrentTime,
    onRegionCreated: (start, end) => {
      setAddSegmentStart(start);
      setAddSegmentEnd(end);
      setAddSegmentOpen(true);
    },
    isMobile,
  });

  const zoomIndex = Math.max(0, ZOOM_LEVELS.indexOf(playerState.zoom as typeof ZOOM_LEVELS[number]));
  const hasMedia = !!(state.task?.videoUrl || state.task?.audioUrl);

  // ── Utterance actions ────────────────────────────────────────────────────

  const handleUpdateUtterance = useCallback(
    async (utteranceId: string, updates: Partial<Utterance>) => {
      if (!canEdit) return;
      updateUtteranceLocal(utteranceId, updates);
      const save = pendingEdits.current.then(async () => {
        const payload = { ...failedEdits.current.get(utteranceId), ...updates };
        try {
          await annotationApi.updateUtterance(taskId, utteranceId, payload);
          failedEdits.current.delete(utteranceId);
        } catch (err) {
          failedEdits.current.set(utteranceId, payload);
          addToast('error', err instanceof Error ? err.message : 'Failed to update utterance.');
        }
      });
      pendingEdits.current = save;
      await save;
    },
    [canEdit, taskId, updateUtteranceLocal, addToast],
  );

  const handleDeleteUtterance = useCallback(
    async (utteranceId: string) => {
      if (!canEdit) return;
      removeUtteranceLocal(utteranceId);
      try {
        await annotationApi.deleteUtterance(taskId, utteranceId);
      } catch (err) {
        addToast('error', err instanceof Error ? err.message : 'Failed to delete utterance.');
        // Refetch to restore state
        refetch();
      }
    },
    [canEdit, taskId, removeUtteranceLocal, addToast, refetch],
  );

  const handleAddSegment = useCallback(
    (start?: number, end?: number) => {
      setAddSegmentStart(start ?? currentTime);
      setAddSegmentEnd(end ?? Math.max((start ?? currentTime) + 5, currentTime + 5));
      setAddSegmentOpen(true);
    },
    [currentTime],
  );

  const handleAddSegmentSave = useCallback(
    async (data: {
      speaker: Utterance['speaker'];
      startTime: number;
      endTime: number;
      transcript?: string;
    }) => {
      setAddSegmentOpen(false);
      clearDragRegion();
      if (!state.task) return;
      if (!canEdit) {
        addToast('error', 'Cannot add segments: task is not editable in its current status.');
        return;
      }
      try {
        const newUtterance = await annotationApi.createUtterance(taskId, {
          ...data,
          taskId,
          sessionId: state.task.sessionId,
          sequenceIndex: state.task.utterances.length,
          speechMarkers: [],
          entities: [],
        } as Partial<Utterance>);
        addUtteranceLocal(newUtterance);
        selectUtterance(newUtterance.utteranceId);
      } catch (err) {
        addToast('error', err instanceof Error ? err.message : 'Failed to add segment.');
      }
    },
    [canEdit, taskId, state.task, addUtteranceLocal, selectUtterance, addToast],
  );

  const handleSplitUtterance = useCallback(
    async (utteranceId: string, splitTime: number) => {
      if (!canEdit || !state.task) return;
      const original = state.task.utterances.find((u) => u.utteranceId === utteranceId);
      if (!original) return;
      try {
        // Update original to end at split time
        await annotationApi.updateUtterance(taskId, utteranceId, { endTime: splitTime });
        updateUtteranceLocal(utteranceId, { endTime: splitTime });
        // Create new utterance from split point
        const newUtterance = await annotationApi.createUtterance(taskId, {
          speaker: original.speaker,
          startTime: splitTime,
          endTime: original.endTime,
          taskId,
          sessionId: original.sessionId,
          sequenceIndex: original.sequenceIndex + 1,
          speechMarkers: [],
          entities: [],
        } as Partial<Utterance>);
        addUtteranceLocal(newUtterance);
      } catch (err) {
        addToast('error', err instanceof Error ? err.message : 'Failed to split utterance.');
      }
    },
    [canEdit, taskId, state.task, updateUtteranceLocal, addUtteranceLocal, addToast],
  );

  // ── Entity actions ───────────────────────────────────────────────────────

  const handleAddEntity = useCallback(
    async (
      utteranceId: string,
      data: { entityType: EntityType; entityName: string; sentiment?: EntitySentiment },
    ) => {
      if (!canEdit) return;
      try {
        const entity = await annotationApi.createEntity(utteranceId, data);
        addEntityLocal(utteranceId, entity);
      } catch (err) {
        addToast('error', err instanceof Error ? err.message : 'Failed to add entity.');
      }
    },
    [canEdit, addEntityLocal, addToast],
  );

  const handleDeleteEntity = useCallback(
    async (utteranceId: string, entityId: string) => {
      if (!canEdit) return;
      removeEntityLocal(utteranceId, entityId);
      try {
        await annotationApi.deleteEntity(utteranceId, entityId);
      } catch (err) {
        addToast('error', err instanceof Error ? err.message : 'Failed to delete entity.');
        refetch();
      }
    },
    [canEdit, removeEntityLocal, addToast, refetch],
  );

  // ── Photo label actions ──────────────────────────────────────────────────

  const handleUpdatePhotoLabels = useCallback(
    async (photoId: string, labels: Parameters<typeof annotationApi.upsertPhotoLabels>[1], photoType?: PhotoType | null) => {
      try {
        const result = await annotationApi.upsertPhotoLabels(photoId, labels, photoType);
        updatePhotoLabelsLocal(photoId, result.labels);
        if (photoType !== undefined) {
          updatePhotoTypeLocal(photoId, result.photoType ?? null);
        }
      } catch (err) {
        addToast('error', err instanceof Error ? err.message : 'Failed to save photo labels.');
      }
    },
    [addToast, updatePhotoLabelsLocal, updatePhotoTypeLocal],
  );

  const handleSupervisorPhotoReview = useCallback(
    async (photoId: string, verified: boolean, notes: string) => {
      try {
        await annotationApi.reviewPhoto(photoId, { verified, notes });
        qc.invalidateQueries({ queryKey: ['annotation-task', taskId] });
      } catch (err) {
        addToast('error', err instanceof Error ? err.message : 'Failed to submit photo review.');
      }
    },
    [qc, taskId, addToast],
  );

  // ── Submit ───────────────────────────────────────────────────────────────

  const handleSubmitConfirm = useCallback(async () => {
    setSubmitConfirmOpen(false);
    try {
      await submitMutation.mutateAsync(taskId);
      addToast('success', 'Task submitted for review.');
    } catch (err) {
      addToast('error', err instanceof Error ? err.message : 'Failed to submit task.');
    }
  }, [taskId, submitMutation, addToast]);

  // ── Review ───────────────────────────────────────────────────────────────

  const handleReview = useCallback(
    async (
      status: 'approved' | 'rejected' | 'revision_needed',
      comments: string,
    ) => {
      await reviewMutation.mutateAsync({ taskId, data: { status, comments } });
      addToast('success', `Task ${status === 'approved' ? 'approved' : status === 'rejected' ? 'rejected' : 'marked for revision'}.`);
    },
    [taskId, reviewMutation, addToast],
  );

  const handleFinalReview = useCallback(
    async (
      status: 'final_accepted' | 'final_rejected' | 'revision_needed',
      comments: string,
    ) => {
      setFinalizingReview(true);
      try {
        await pendingEdits.current;
        if (failedEdits.current.size) {
          throw new Error('A correction failed to save. Re-save the affected segment before final review.');
        }
        await finalReviewMutation.mutateAsync({ taskId, data: { status, comments } });
      } finally {
        setFinalizingReview(false);
      }
      const msg =
        status === 'final_accepted'
          ? 'Task finally accepted.'
          : status === 'final_rejected'
          ? 'Task finally rejected.'
          : 'Task returned to supervisor for further revision.';
      addToast('success', msg);
    },
    [taskId, finalReviewMutation, addToast],
  );

  const handleReopen = useCallback(
    async (
      target: 'submitted' | 'revision_needed' | 'approved',
      comments: string,
    ) => {
      await reopenMutation.mutateAsync({ taskId, data: { target, comments } });
      const label =
        target === 'submitted'
          ? 'supervisor review queue'
          : target === 'revision_needed'
          ? 'transcriber as revision needed'
          : 'sub-admin final review queue';
      addToast('success', `Task reopened to ${label}.`);
    },
    [taskId, reopenMutation, addToast],
  );

  // ── Keyboard shortcuts ───────────────────────────────────────────────────

  const utterances = state.task?.utterances ?? [];
  const currentIndex = selectedUtterance
    ? utterances.findIndex((u) => u.utteranceId === selectedUtterance.utteranceId)
    : -1;

  useKeyboardShortcuts(
    {
      onEscape: () => selectUtterance(null),
      onShowHelp: () => setShortcutsOpen(true),
      onManualSave: saveNow,
      onSubmit: () => {
        if (canSubmit) setSubmitConfirmOpen(true);
      },
      onPrevUtterance: () => {
        if (currentIndex > 0)
          selectUtterance(utterances[currentIndex - 1].utteranceId);
      },
      onNextUtterance: () => {
        if (currentIndex < utterances.length - 1)
          selectUtterance(utterances[currentIndex + 1].utteranceId);
      },
    },
    true,
  );

  // ── Loading / error ──────────────────────────────────────────────────────

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="w-8 h-8 animate-spin text-violet-600" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-3">
        <AlertCircle className="w-10 h-10 text-red-500" />
        <p className="text-gray-600 font-medium">Failed to load task.</p>
        <button
          onClick={() => refetch()}
          className="flex items-center gap-2 px-4 py-2 text-sm rounded-lg font-medium bg-violet-600 text-white hover:bg-violet-700 transition-colors"
        >
          <RefreshCw className="w-4 h-4" />
          Retry
        </button>
      </div>
    );
  }

  if (!state.task) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="w-8 h-8 animate-spin text-violet-600" />
      </div>
    );
  }

  const { task: wsTask } = state;
  const allEntityNames = utterances.flatMap((u) => u.entities.map((e) => e.entityName));

  const canSubmit =
    !!taskStatus &&
    utterances.length > 0 &&
    ['pending', 'in_progress', 'revision_needed'].includes(taskStatus) &&
    (isEnumerator || isSupervisorOrAdmin);

  // ── Render ───────────────────────────────────────────────────────────────

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Toast notifications */}
      <div className="fixed top-4 right-4 z-50 space-y-2 pointer-events-none">
        {toasts.map((t) => (
          <div
            key={t.id}
            className={`px-4 py-2.5 rounded-xl shadow-lg text-sm font-medium pointer-events-auto ${
              t.type === 'success'
                ? 'bg-green-600 text-white'
                : 'bg-red-600 text-white'
            }`}
          >
            {t.message}
          </div>
        ))}
      </div>

      {/* Header */}
      <header className="bg-white border-b border-gray-200 flex-shrink-0 flex flex-col sm:flex-row sm:h-14 sm:items-center sm:px-4 sm:gap-3">
        {/* Row 1: always visible — on desktop sm:contents makes this wrapper invisible so children join the header flex row */}
        <div className="h-11 flex items-center px-3 sm:px-0 sm:contents gap-2 sm:gap-3">
          <Link
            to="/annotation/tasks"
            className="flex items-center gap-1.5 text-gray-500 hover:text-gray-700 text-sm transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="hidden sm:inline">Back</span>
          </Link>

          <div className="h-5 w-px bg-gray-200" />

          <div className="flex items-center gap-2 min-w-0">
            <span className="font-mono text-xs bg-gray-100 text-gray-700 px-2 py-0.5 rounded truncate max-w-32">
              {wsTask.sessionId?.slice(0, 16) ?? '—'}
            </span>
            {wsTask.respondentName && (
              <span className="text-sm text-gray-600 truncate hidden md:block">
                {wsTask.respondentName}
              </span>
            )}
            <StatusBadge status={wsTask.status} />
          </div>

          <div className="flex-1" />

          <AutoSaveIndicator
            status={state.autoSaveStatus}
            savedAt={state.autoSavedAt}
          />

          {/* Auto-Transcribe — hidden on mobile (available via header row 2 context) */}
          {isSupervisorOrAdmin && !['approved', 'rejected'].includes(taskStatus ?? '') && (
            <button
              onClick={() => setTranscribeDialogOpen(true)}
              disabled={isTranscribing || triggerPending}
              className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg font-medium bg-purple-600 hover:bg-purple-700 text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              title="Auto-transcribe audio using Whisper"
            >
              {(isTranscribing || triggerPending) ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <Wand2 className="w-3.5 h-3.5" />
              )}
              <span>
                {isTranscribing ? 'Transcribing…'
                  : transcriptionStatus === 'completed' ? 'Re-Transcribe'
                  : 'Auto-Transcribe'}
              </span>
            </button>
          )}

          {/* Submit button */}
          {(isEnumerator || isSupervisorOrAdmin) && ['pending', 'in_progress', 'revision_needed'].includes(taskStatus ?? '') && (
            <button
              onClick={() => setSubmitConfirmOpen(true)}
              disabled={!canSubmit || submitMutation.isPending}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg font-medium bg-violet-600 hover:bg-violet-700 text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              title="Submit for review (Ctrl+Enter)"
            >
              {submitMutation.isPending ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <Send className="w-3.5 h-3.5" />
              )}
              <span className="hidden sm:inline">Submit</span>
            </button>
          )}

          {/* Desktop mode tabs — hidden on mobile (shown in row 2 below) */}
          <button
            onClick={() => { setShowPhotos(false); setShowTranscripts(false); }}
            className={`hidden sm:flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg font-medium transition-colors ${
              !showPhotos && !showTranscripts
                ? 'bg-violet-100 text-violet-700 border border-violet-200'
                : 'bg-white border border-gray-200 hover:bg-gray-50 text-gray-700'
            }`}
          >
            <Mic className="w-4 h-4" />
            <span>Transcription</span>
          </button>

          <button
            onClick={() => { setShowPhotos((v) => !v); setShowTranscripts(false); }}
            className={`hidden sm:flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg font-medium transition-colors ${
              showPhotos
                ? 'bg-blue-100 text-blue-700 border border-blue-200'
                : 'bg-white border border-gray-200 hover:bg-gray-50 text-gray-700'
            }`}
          >
            <Camera className="w-4 h-4" />
            <span>Photos {wsTask.photos.length > 0 && `(${wsTask.photos.length})`}</span>
          </button>

          {wsTask.transcripts.length > 0 && (
            <button
              onClick={() => { setShowTranscripts((v) => !v); setShowPhotos(false); }}
              className={`hidden sm:flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg font-medium transition-colors ${
                showTranscripts
                  ? 'bg-emerald-100 text-emerald-700 border border-emerald-200'
                  : 'bg-white border border-gray-200 hover:bg-gray-50 text-gray-700'
              }`}
            >
              <FileText className="w-4 h-4" />
              <span>Transcripts ({wsTask.transcripts.length})</span>
            </button>
          )}

          {/* Shortcuts help — desktop only */}
          <button
            onClick={() => setShortcutsOpen(true)}
            className="hidden sm:flex p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            title="Keyboard shortcuts (?)"
          >
            <HelpCircle className="w-5 h-5" />
          </button>
        </div>

        {/* Row 2: mobile-only mode tabs */}
        <div className="flex sm:hidden border-t border-gray-100 px-3 pb-1.5 pt-1 gap-1.5">
          <button
            onClick={() => { setShowPhotos(false); setShowTranscripts(false); }}
            className={`flex-1 flex items-center justify-center gap-1 py-1 rounded-lg text-xs font-medium transition-colors ${
              !showPhotos && !showTranscripts ? 'bg-violet-100 text-violet-700' : 'bg-gray-50 text-gray-500'
            }`}
          >
            <Mic className="w-3.5 h-3.5" />
            Transcription
          </button>
          <button
            onClick={() => { setShowPhotos((v) => !v); setShowTranscripts(false); }}
            className={`flex-1 flex items-center justify-center gap-1 py-1 rounded-lg text-xs font-medium transition-colors ${
              showPhotos ? 'bg-blue-100 text-blue-700' : 'bg-gray-50 text-gray-500'
            }`}
          >
            <Camera className="w-3.5 h-3.5" />
            Photos
          </button>
          {wsTask.transcripts.length > 0 && (
            <button
              onClick={() => { setShowTranscripts((v) => !v); setShowPhotos(false); }}
              className={`flex-1 flex items-center justify-center gap-1 py-1 rounded-lg text-xs font-medium transition-colors ${
                showTranscripts ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-50 text-gray-500'
              }`}
            >
              <FileText className="w-3.5 h-3.5" />
              Transcripts
            </button>
          )}
        </div>
      </header>

      {/* ── Claim / Unclaim banner ──────────────────────────────────────── */}
      {/* Surfaces ownership of in-flight work. Transcribers/enumerators must
          explicitly claim a task to lock it to themselves; supervisors and
          admins can unclaim to free a stuck task. Hidden on terminal statuses
          where the claim no longer matters. */}
      {!['final_accepted', 'final_rejected'].includes(wsTask.status) && (() => {
        const claimedBy = (wsTask as { claimedBy?: string | null }).claimedBy ?? null;
        const claimedByName = (wsTask as { claimedByName?: string | null }).claimedByName ?? null;
        const iClaimed = !!claimedBy && claimedBy === meId;
        const claimedBySomeoneElse = !!claimedBy && claimedBy !== meId;
        const canClaim = !claimedBy && (isEnumerator || isSupervisorOrAdmin);
        const canUnclaim = claimedBySomeoneElse && isSupervisorOrAdmin;

        if (!claimedBy && !canClaim) return null;

        return (
          <div
            className={`flex-shrink-0 flex items-center gap-3 px-4 py-2 text-sm border-b ${
              iClaimed
                ? 'bg-emerald-50 border-emerald-200 text-emerald-900'
                : claimedBySomeoneElse
                ? 'bg-amber-50 border-amber-200 text-amber-900'
                : 'bg-violet-50 border-violet-200 text-violet-900'
            }`}
          >
            <span className="flex-1 truncate">
              {iClaimed
                ? 'You have claimed this task. Only you (and a supervisor/admin) can edit it.'
                : claimedBySomeoneElse
                ? `Claimed by ${claimedByName ?? 'another user'}. ${
                    canUnclaim
                      ? 'You can unclaim it to release ownership.'
                      : 'You cannot edit until they release it.'
                  }`
                : 'This task is unclaimed. Claim it before you start to prevent another user from taking it over.'}
            </span>
            {canClaim && (
              <button
                onClick={() => claimMutation.mutate(taskId)}
                disabled={claimMutation.isPending}
                className="px-3 py-1.5 text-xs rounded-md font-medium bg-violet-600 hover:bg-violet-700 text-white disabled:opacity-50"
              >
                {claimMutation.isPending ? 'Claiming…' : 'Claim This Task'}
              </button>
            )}
            {canUnclaim && (
              <button
                onClick={() => unclaimMutation.mutate(taskId)}
                disabled={unclaimMutation.isPending}
                className="px-3 py-1.5 text-xs rounded-md font-medium bg-amber-600 hover:bg-amber-700 text-white disabled:opacity-50"
              >
                {unclaimMutation.isPending ? 'Unclaiming…' : 'Unclaim'}
              </button>
            )}
          </div>
        );
      })()}

      {/* ── Transcription status banner ─────────────────────────────────── */}
      {transcriptionStatus !== 'none' && (
        <div className={`flex-shrink-0 flex items-center gap-2.5 px-4 py-2 text-sm border-b ${
          transcriptionStatus === 'queued' || transcriptionStatus === 'running'
            ? 'bg-purple-50 border-purple-200 text-purple-800'
            : transcriptionStatus === 'completed'
            ? 'bg-green-50 border-green-200 text-green-800'
            : 'bg-red-50 border-red-200 text-red-800'
        }`}>
          {(transcriptionStatus === 'queued' || transcriptionStatus === 'running') && (
            <Loader2 className="w-4 h-4 animate-spin flex-shrink-0" />
          )}
          {transcriptionStatus === 'completed' && (
            <CheckCircle className="w-4 h-4 flex-shrink-0" />
          )}
          {transcriptionStatus === 'failed' && (
            <AlertCircle className="w-4 h-4 flex-shrink-0" />
          )}
          <span className="font-medium">
            {transcriptionStatus === 'queued'
              ? 'Transcription job queued — waiting for GPU…'
              : transcriptionStatus === 'running'
              ? 'Transcribing audio (Hindi)… usually 5–15 minutes'
              : transcriptionStatus === 'completed'
              ? 'Auto-transcription complete — review and correct segments below'
              : `Transcription failed: ${transcriptionJob?.errorMessage ?? 'unknown error'}`}
          </span>
        </div>
      )}

      {/* ── Full-width waveform bar — hidden in photos/transcripts mode ──── */}
      <div className={`flex-shrink-0 bg-white border-b border-gray-200 px-3 py-2${showPhotos || showTranscripts ? ' hidden' : ''}`}>
        {/* CSS grid: single column on mobile (stacks rows), three columns on desktop */}
        <div className="grid grid-cols-1 sm:grid-cols-[auto_1fr_auto] items-center gap-2 sm:gap-3">

          {/* Playback controls — space-between row on mobile, stacked column on desktop */}
          <div className="flex items-center justify-between sm:flex-col sm:items-center sm:justify-center sm:gap-1 sm:pr-3 sm:border-r sm:border-gray-200">
            {/* Transport buttons */}
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={() => skip(-5)}
                disabled={!hasMedia}
                className="p-1.5 rounded-lg text-gray-600 hover:bg-gray-100 transition-colors disabled:opacity-40"
                title="Back 5s"
              >
                <SkipBack className="w-4 h-4" />
              </button>
              <button
                type="button"
                onClick={playPause}
                disabled={!hasMedia}
                className="p-2 rounded-full bg-violet-600 text-white hover:bg-violet-700 transition-colors shadow disabled:opacity-40"
                title={playerState.isPlaying ? 'Pause' : 'Play'}
              >
                {playerState.isPlaying
                  ? <Pause className="w-4 h-4" />
                  : <Play className="w-4 h-4 ml-0.5" />}
              </button>
              <button
                type="button"
                onClick={() => skip(5)}
                disabled={!hasMedia}
                className="p-1.5 rounded-lg text-gray-600 hover:bg-gray-100 transition-colors disabled:opacity-40"
                title="Forward 5s"
              >
                <SkipForward className="w-4 h-4" />
              </button>
            </div>
            {/* Timestamp */}
            <span className="text-xs font-mono text-gray-500 tabular-nums whitespace-nowrap">
              <TimestampDisplay seconds={playerState.currentTime} />
              {' / '}
              <TimestampDisplay seconds={playerState.duration} />
            </span>
            {/* Speed buttons */}
            <div className="flex items-center gap-0.5">
              {PLAYBACK_RATES.map((r) => (
                <button
                  key={r.value}
                  type="button"
                  onClick={() => setPlaybackRate(r.value)}
                  className={[
                    'px-1.5 py-0.5 text-xs rounded font-medium transition-colors',
                    playerState.playbackRate === r.value
                      ? 'bg-violet-600 text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200',
                  ].join(' ')}
                >
                  {r.label}
                </button>
              ))}
            </div>
          </div>

          {/* Waveform — single ref, full-width row on mobile, middle column on desktop */}
          <div className="min-w-0">
            <div className="relative rounded-lg overflow-hidden bg-gray-50 border border-gray-100" style={{ minHeight: isMobile ? 48 : 72 }}>
              <div ref={waveformBarRef} className="overflow-x-auto" style={{ minHeight: isMobile ? 48 : 72 }} />
              {hasMedia && !playerState.isReady && (
                <div className="absolute inset-0 z-10 flex items-center justify-center gap-2 bg-gray-50/80">
                  <Loader2 className="w-4 h-4 animate-spin text-violet-500" />
                  <span className="text-xs text-gray-500">Loading waveform…</span>
                </div>
              )}
            </div>
            <p className="text-xs text-gray-400 text-center mt-0.5 hidden sm:block">
              {playerState.isReady ? 'Drag on waveform to select a region' : 'You can start playing while the waveform loads'}
            </p>
          </div>

          {/* Zoom (desktop only) + Add Segment */}
          <div className="flex items-center justify-end sm:flex-col sm:items-center sm:gap-2 sm:pl-3 sm:border-l sm:border-gray-200">
            <div className="hidden sm:flex items-center gap-1">
              <button
                type="button"
                onClick={() => zoom(ZOOM_LEVELS[Math.max(0, zoomIndex - 1)])}
                disabled={zoomIndex === 0}
                className="p-1 rounded text-gray-500 hover:bg-gray-100 disabled:opacity-30 transition-colors"
                title="Zoom out"
              >
                <ZoomOut className="w-3.5 h-3.5" />
              </button>
              <input
                type="range"
                min={0}
                max={ZOOM_LEVELS.length - 1}
                step={1}
                value={zoomIndex}
                onChange={(e) => zoom(ZOOM_LEVELS[Number(e.target.value)])}
                className="w-24 h-1 accent-violet-600"
                title={zoomIndex === 0 ? 'Zoom: Fit' : `Zoom: ${zoomIndex}×`}
              />
              <button
                type="button"
                onClick={() => zoom(ZOOM_LEVELS[Math.min(ZOOM_LEVELS.length - 1, zoomIndex + 1)])}
                disabled={zoomIndex === ZOOM_LEVELS.length - 1}
                className="p-1 rounded text-gray-500 hover:bg-gray-100 disabled:opacity-30 transition-colors"
                title="Zoom in"
              >
                <ZoomIn className="w-3.5 h-3.5" />
              </button>
              <span className="text-xs text-gray-400 w-8 tabular-nums">
                {zoomIndex === 0 ? 'Fit' : `${zoomIndex}×`}
              </span>
            </div>
            <button
              type="button"
              onClick={() => handleAddSegment()}
              className="flex items-center justify-center gap-1.5 px-3 py-1.5 text-xs rounded-lg bg-violet-50 text-violet-700 hover:bg-violet-100 border border-violet-200 transition-colors font-medium whitespace-nowrap"
            >
              <Plus className="w-3.5 h-3.5" />
              Add Segment
            </button>
          </div>
        </div>
      </div>

      {/* Main workspace area — hidden in photos/transcripts mode, kept in DOM for video element */}
      <div className={`flex-1 flex flex-col sm:flex-row min-h-0 overflow-hidden${showPhotos || showTranscripts ? ' hidden' : ''}`}>
        {/* Video panel — hidden on mobile */}
        <div className="hidden sm:block w-56 flex-shrink-0 p-3 overflow-y-auto">
          <VideoPlayerPanel
            videoRef={videoRef}
            videoUrl={wsTask.videoUrl}
            audioUrl={wsTask.audioUrl}
          />
        </div>

        {/* Transcript panel — full width on mobile, toggled by mobilePanel */}
        <div className={`${mobilePanel === 'transcript' ? 'flex' : 'hidden'} sm:flex flex-col flex-1 p-3 pb-16 sm:pb-3 min-w-0 overflow-hidden`}>
          <TranscriptPanel
            utterances={utterances}
            selectedUtteranceId={state.selectedUtteranceId}
            isReviewMode={isReviewMode}
            transcriptionStatus={transcriptionStatus}
            onSelectUtterance={selectUtterance}
            onUpdateUtterance={handleUpdateUtterance}
            onDeleteUtterance={handleDeleteUtterance}
            onPlaySegment={(start, end) => { highlightRegion(start, end); toggleSegment(start, end); }}
            onAddSegment={handleAddSegment}
            onSplitUtterance={handleSplitUtterance}
            currentTime={currentTime}
            isPlaying={playerState.isPlaying}
          />
        </div>

        {/* Annotation + Review panel — full width on mobile, toggled by mobilePanel */}
        <div className={`${mobilePanel === 'annotation' ? 'flex' : 'hidden'} sm:flex flex-col w-full sm:w-72 xl:w-80 flex-shrink-0 p-3 pb-16 sm:pb-3 gap-3 overflow-y-auto`}>
          <div className="flex-1 min-h-0">
            <AnnotationPanel
              utterance={selectedUtterance}
              isReviewMode={isReviewMode}
              onUpdate={handleUpdateUtterance}
              onAddEntity={handleAddEntity}
              onDeleteEntity={handleDeleteEntity}
              allEntityNames={allEntityNames}
            />
          </div>

          {/* Review panel for supervisor/admin (acts on 'submitted' tasks) */}
          {isSupervisorOrAdmin && wsTask.status === 'submitted' && (
            <ReviewPanel
              taskId={taskId}
              currentStatus={wsTask.status}
              latestReview={
                wsTask.latestReview
                  ? {
                      status: wsTask.latestReview.status,
                      comments: wsTask.latestReview.comments ?? undefined,
                    }
                  : undefined
              }
              onReview={handleReview}
            />
          )}

          {/* Final review panel for sub-admin/admin (acts on 'approved' tasks) */}
          {isSubAdminOrAdmin && wsTask.status === 'approved' && (
            <FinalReviewPanel
              taskId={taskId}
              currentStatus={wsTask.status}
              latestReview={
                wsTask.latestReview
                  ? {
                      status: wsTask.latestReview.status,
                      comments: wsTask.latestReview.comments ?? undefined,
                    }
                  : undefined
              }
              onFinalReview={handleFinalReview}
            />
          )}

          {/* Admin rescue panel (admin only, terminal rejected states) */}
          {role === 'admin' &&
            (wsTask.status === 'rejected' || wsTask.status === 'final_rejected') && (
            <AdminRescuePanel
              currentStatus={wsTask.status}
              latestReview={
                wsTask.latestReview
                  ? {
                      status: wsTask.latestReview.status,
                      comments: wsTask.latestReview.comments ?? undefined,
                    }
                  : undefined
              }
              onReopen={handleReopen}
            />
          )}
        </div>

        {/* Mobile bottom tab bar — switches between Transcript and Labels panels */}
        <div className="sm:hidden fixed bottom-0 left-0 right-0 z-40 h-14 bg-white border-t border-gray-200 flex">
          <button
            onClick={() => setMobilePanel('transcript')}
            className={`flex-1 flex flex-col items-center justify-center gap-0.5 text-xs font-medium transition-colors ${
              mobilePanel === 'transcript' ? 'text-violet-700' : 'text-gray-400'
            }`}
          >
            <AlignLeft className="w-5 h-5" />
            Transcript
          </button>
          <button
            onClick={() => setMobilePanel('annotation')}
            className={`flex-1 flex flex-col items-center justify-center gap-0.5 text-xs font-medium transition-colors ${
              mobilePanel === 'annotation' ? 'text-violet-700' : 'text-gray-400'
            }`}
          >
            <Tag className="w-5 h-5" />
            Labels
          </button>
        </div>
      </div>

      {/* Photos mode — full two-pane layout: photo viewer left, label form right */}
      {showPhotos && (
        <div className="flex-1 min-h-0 p-3 overflow-hidden">
          <PhotoAnnotationPanel
            photos={wsTask.photos}
            isReviewMode={isReviewMode}
            onUpdateLabels={handleUpdatePhotoLabels}
            onSupervisorReview={
              isSupervisorOrAdmin ? handleSupervisorPhotoReview : undefined
            }
            horizontal={!isMobile}
          />
        </div>
      )}

      {/* Transcripts mode — full-width scrollable image viewer */}
      {showTranscripts && (
        <div className="flex-1 min-h-0 overflow-y-auto bg-gray-50 p-4">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-sm font-semibold text-gray-700">
                Interview Transcripts — {wsTask.transcripts.length} page{wsTask.transcripts.length !== 1 ? 's' : ''}
              </h2>
              <a
                href={wsTask.transcripts[0]?.url ?? '#'}
                download
                onClick={(e) => {
                  e.preventDefault();
                  wsTask.transcripts.forEach((t, i) => {
                    if (!t.url) return;
                    const a = document.createElement('a');
                    a.href = t.url;
                    a.download = `transcript_page_${String(i + 1).padStart(2, '0')}.jpg`;
                    a.click();
                  });
                }}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg font-medium bg-emerald-600 hover:bg-emerald-700 text-white transition-colors"
              >
                <Download className="w-3.5 h-3.5" />
                Download All
              </a>
            </div>
            <div className="space-y-6">
              {wsTask.transcripts
                .slice()
                .sort((a, b) => a.pageNumber - b.pageNumber)
                .map((t) => (
                  <div key={t.transcriptId} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                    <div className="flex items-center justify-between px-4 py-2 border-b border-gray-100 bg-gray-50">
                      <span className="text-xs font-medium text-gray-600">
                        Page {t.pageNumber}
                      </span>
                      {t.url && (
                        <a
                          href={t.url}
                          download={`transcript_page_${String(t.pageNumber).padStart(2, '0')}.jpg`}
                          className="flex items-center gap-1 text-xs text-emerald-600 hover:text-emerald-800 font-medium transition-colors"
                        >
                          <Download className="w-3 h-3" />
                          Download
                        </a>
                      )}
                    </div>
                    {t.url ? (
                      <img
                        src={t.url}
                        alt={`Transcript page ${t.pageNumber}`}
                        className="w-full object-contain"
                        loading="lazy"
                      />
                    ) : (
                      <div className="flex items-center justify-center h-48 text-gray-400 text-sm">
                        Image not available
                      </div>
                    )}
                  </div>
                ))}
            </div>
          </div>
        </div>
      )}

      {/* Modals */}
      <AddSegmentModal
        open={addSegmentOpen}
        initialStart={addSegmentStart}
        initialEnd={addSegmentEnd}
        onClose={() => { setAddSegmentOpen(false); clearDragRegion(); }}
        onSave={handleAddSegmentSave}
      />

      <KeyboardShortcutsOverlay
        open={shortcutsOpen}
        onClose={() => setShortcutsOpen(false)}
      />

      <ConfirmDialog
        open={submitConfirmOpen}
        title="Submit for Review"
        message="Submit this session for review? You won't be able to edit it after submission."
        confirmLabel="Submit"
        onConfirm={handleSubmitConfirm}
        onCancel={() => setSubmitConfirmOpen(false)}
      />

      {transcribeDialogOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-sm p-5">
            <h3 className="text-base font-semibold text-gray-900">
              {transcriptionStatus === 'completed' ? 'Re-Transcribe' : 'Auto-Transcribe'}
            </h3>
            <p className="text-xs text-gray-500 mt-1">
              Whisper transcribes the full recording; speakers are detected automatically.
              {transcriptionStatus === 'completed' && ' Your corrected segments are kept; only auto segments are replaced.'}
            </p>

            <label className="block text-xs font-medium text-gray-600 mt-4 mb-1">Language</label>
            <select
              className="input w-full"
              value={transcribeLang}
              onChange={(e) => setTranscribeLang(e.target.value)}
            >
              <option value="hi">Hindi (default)</option>
              <option value="auto">Auto-detect</option>
              <option value="en">English</option>
              <option value="bn">Bengali</option>
              <option value="mr">Marathi</option>
              <option value="ta">Tamil</option>
              <option value="te">Telugu</option>
              <option value="gu">Gujarati</option>
              <option value="pa">Punjabi</option>
              <option value="ur">Urdu</option>
              <option value="kn">Kannada</option>
              <option value="ml">Malayalam</option>
              <option value="or">Odia</option>
              <option value="ne">Nepali</option>
            </select>

            <label className="block text-xs font-medium text-gray-600 mt-3 mb-1">
              Max speakers <span className="text-gray-400">(optional — blank = auto)</span>
            </label>
            <input
              type="number"
              min={1}
              max={10}
              className="input w-full"
              placeholder="Auto"
              value={transcribeMaxSpk}
              onChange={(e) => setTranscribeMaxSpk(e.target.value)}
            />

            <div className="mt-5 flex justify-end gap-2">
              <button
                className="px-3 py-1.5 rounded-md text-sm font-medium bg-gray-100 text-gray-700 hover:bg-gray-200"
                onClick={() => setTranscribeDialogOpen(false)}
                disabled={triggerPending}
              >
                Cancel
              </button>
              <button
                className="px-3 py-1.5 rounded-md text-sm font-medium bg-purple-600 hover:bg-purple-700 text-white disabled:opacity-50"
                disabled={triggerPending}
                onClick={() => {
                  const maxSpk = parseInt(transcribeMaxSpk, 10);
                  setTranscribeDialogOpen(false);
                  handleTriggerTranscription({
                    language: transcribeLang || undefined,
                    maxSpeakers: Number.isFinite(maxSpk) && maxSpk > 0 ? maxSpk : undefined,
                  });
                }}
              >
                {triggerPending ? 'Starting…' : 'Start'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Page Wrapper (provides AnnotationContext) ─────────────────────────────────

export default function AnnotationWorkspacePage() {
  const { taskId } = useParams<{ taskId: string }>();

  if (!taskId) {
    return (
      <div className="flex items-center justify-center h-full">
        <p className="text-gray-500">Invalid task ID.</p>
      </div>
    );
  }

  return (
    <AnnotationProvider>
      <WorkspaceInner taskId={taskId} />
    </AnnotationProvider>
  );
}
